import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Wrap, Heading, Viewport, Slide, PosterFrame, Poster, Title, Rank,
  BookBtn, Dots, Dot, Empty, Spinner,
} from './HomeMobileStyles';

// cinetree 모바일 메인 (모바일 폭에서만 렌더 — index.js 뷰포트 분기).
// 상영작 포스터 캐러셀 + 예매하기. 데이터는 기존 /movieInfo/fetch-movie-info 재사용.
export default function HomeMobile() {
  const navigate = useNavigate();
  const [movies, setMovies] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [idx, setIdx] = useState(0);
  const [posterStatus, setPosterStatus] = useState({}); // { [movie_code]: 'loading' | 'loaded' | 'error' }
  const viewportRef = useRef(null);

  useEffect(() => {
    let alive = true;
    fetch('/movieInfo/fetch-movie-info', { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : []))
      .then((data) => {
        if (!alive) return;
        const list = Array.isArray(data)
          ? data.filter((m) => m && m.poster).sort((a, b) => (a.rank || 0) - (b.rank || 0)).slice(0, 10)
          : [];
        setMovies(list);
        setPosterStatus(list.reduce((a, m) => { a[m.movie_code] = 'loading'; return a; }, {}));
        setLoaded(true);
      })
      .catch(() => { if (alive) setLoaded(true); });
    return () => { alive = false; };
  }, []);

  const onScroll = () => {
    const el = viewportRef.current;
    if (!el || !el.clientWidth) return;
    const i = Math.round(el.scrollLeft / el.clientWidth);
    if (i !== idx) setIdx(i);
  };

  const goTo = (i) => {
    const el = viewportRef.current;
    if (el) el.scrollTo({ left: i * el.clientWidth, behavior: 'smooth' });
  };

  const markLoaded = useCallback((c) => setPosterStatus((s) => (s[c] === 'loaded' ? s : { ...s, [c]: 'loaded' })), []);
  const markError = useCallback((c) => setPosterStatus((s) => (s[c] === 'error' ? s : { ...s, [c]: 'error' })), []);

  return (
    <Wrap>
      <Heading>지금 예매 가능한 영화</Heading>

      {movies.length > 0 ? (
        <>
          <Viewport ref={viewportRef} onScroll={onScroll}>
            {movies.map((m, i) => {
              const st = posterStatus[m.movie_code];
              return (
                <Slide key={m.movie_code}>
                  <PosterFrame onClick={() => navigate(`/movie/info/details/${m.movie_code}`)}>
                    {st !== 'loaded' && st !== 'error' && <Spinner />}
                    <Poster
                      src={m.poster}
                      alt={m.title}
                      $loaded={st === 'loaded'}
                      loading={i === 0 ? 'eager' : 'lazy'}
                      fetchpriority={i === 0 ? 'high' : undefined}
                      ref={(node) => { if (node && node.complete && node.naturalWidth > 0) markLoaded(m.movie_code); }}
                      onLoad={() => markLoaded(m.movie_code)}
                      onError={() => markError(m.movie_code)}
                    />
                  </PosterFrame>
                  <Title>{m.title}</Title>
                  {m.rank ? <Rank>예매순위 {m.rank}위</Rank> : null}
                  <BookBtn onClick={() => navigate('/movie/ticketing')}>예매하기</BookBtn>
                </Slide>
              );
            })}
          </Viewport>

          <Dots>
            {movies.map((m, i) => (
              <Dot key={m.movie_code} $on={i === idx} onClick={() => goTo(i)} aria-label={`${i + 1}번째 영화`} />
            ))}
          </Dots>
        </>
      ) : (
        <Empty>{loaded ? '표시할 영화가 없습니다.' : '영화 정보를 불러오는 중…'}</Empty>
      )}
    </Wrap>
  );
}
